import { useCallback, useMemo, type MouseEvent, type ReactNode } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type { Incident, IncidentGroup } from '../../services/incidentsApi';
import IncidentGroupPatternActions from '../../components/incidents/IncidentGroupPatternActions';
import type { IncidentGroupPatternActionMode } from '../../components/incidents/IncidentGroupPatternActionDialog';
import EvidenceCount from '../../components/incidents/EvidenceCount';
import SeverityBadge from '../../components/incidents/SeverityBadge';
import StatusBadge from '../../components/incidents/StatusBadge';
import { getGroupedModeEvidenceQueueCopy, getRawModeEvidenceQueueCopy } from './queueEvidenceLabels';
import {
  incidentDisplaySummaryForRow,
  incidentInsightChipsForRow,
} from './incidentDisplayModel';
import { incidentCanonicalFollowupOwnerLabel } from '../../components/incidents/reviewCanonical';
import { presentAreaLabel } from '../../utils/presentAreaLabel';
import {
  resolveSafetyLocationDisplay,
  type SafetyLocationLookups,
} from '../../utils/locationDisplay';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import { deriveReviewerCaseState } from '../../domain/incidents/reviewerCaseState';
import { REVIEW_OBSERVATION_CTA } from '../../domain/incidents/observationDispositionCopy';
import {
  NO_PARENT_INCIDENT_LABEL,
  observationParentStatusPresentation,
} from '../../domain/incidents/observationEpisodeRow';

type Finding = {
  kind: 'none' | 'chip' | 'text';
  label?: string;
  chipColor?: any;
  chipVariant?: any;
};

type WorkOrderCellRenderer = (
  row: any,
  opts: {
    onCreate: (incidentId: string) => void;
    onOpen: (url: string | undefined) => void;
  },
) => ReactNode;

type UseIncidentsTableColumnsArgs = {
  zoneNameById: Map<string, string>;
  cameraNameById: Map<string, string>;
  deviceNameById: Map<string, string>;
  locationLookups?: SafetyLocationLookups;
  incidentTypeLabel: (raw: any) => string;
  incidentRowTypeLabel: (row: Incident | null | undefined) => string;
  incidentPolicyLabel: (row: Record<string, unknown> | Incident | null | undefined) => string;
  findingForIncident: (incident: Incident | null | undefined) => Finding;
  severityRank: (severity: any) => number;
  severityChipSx: (severity: string) => any;
  severityIcon: (severity: string) => ReactNode;
  openEvidencePopover: (event: MouseEvent<HTMLElement>, row: Incident) => void | Promise<void>;
  openIncidentDetails: (row: Incident | IncidentGroup) => void;
  openIncidentJson: (row: Incident) => void;
  renderWorkOrderCell: WorkOrderCellRenderer;
  handleEnsureOdooTicket: (incidentId: string) => void | Promise<void>;
  handleSyncFromOdoo: (incidentId: string) => void | Promise<void>;
  getOdooStateColor: (state: string | undefined) => string;
  deriveLifecycle: (row: any) => string;
  getOperationalStatusColor: (status: any) => string;
  formatDuration: (seconds?: number | null) => string;
  canManageGroupPatterns?: boolean;
  groupPatternActionBusy?: boolean;
  onGroupPatternAction?: (group: IncidentGroup, mode: IncidentGroupPatternActionMode) => void;
  operatorMode?: boolean;
};

const clampTextSx = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

const formatExactDateTime = (value: unknown) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const dateValue = new Date(raw);
  if (Number.isNaN(dateValue.getTime())) return '—';
  return dateValue.toLocaleString();
};

const formatRelativeTime = (value: unknown) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const dateValue = new Date(raw);
  if (Number.isNaN(dateValue.getTime())) return '—';
  const diffMs = dateValue.getTime() - Date.now();
  const absMinutes = Math.floor(Math.abs(diffMs) / 60000);
  if (absMinutes < 1) return 'Just now';
  if (absMinutes < 60) return `${absMinutes}m ago`;
  const absHours = Math.floor(absMinutes / 60);
  if (absHours < 24) return `${absHours}h ago`;
  const absDays = Math.floor(absHours / 24);
  return `${absDays}d ago`;
};

const resolveLastSeenTimestamp = (row: any): unknown =>
  row?.last_seen_at || row?.last_seen || row?.updated_at || row?.created_at || row?.first_seen;

const timestampSortValue = (row: any): number => {
  const raw = String(resolveLastSeenTimestamp(row) || '').trim();
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const renderLocation = (primary: string, secondary?: string) => (
  <Box sx={{ lineHeight: 1.15 }}>
    <Typography variant="body2" sx={{ fontWeight: 800 }}>
      {primary}
    </Typography>
    {secondary ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
        {secondary}
      </Typography>
    ) : null}
  </Box>
);

const renderTarget = (cameraLabel: string, deviceLabel?: string) => (
  <Box sx={{ lineHeight: 1.15 }}>
    <Typography variant="body2" sx={{ display: 'block', fontWeight: 700 }}>
      {cameraLabel}
    </Typography>
    {deviceLabel ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
        {deviceLabel}
      </Typography>
    ) : null}
  </Box>
);

const renderInsightStrip = (chips: Array<{ label: string; tone?: string }>) => {
  if (!chips.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.6 }}>
      {chips.slice(0, 2).map((chip) => (
        <Box
          key={chip.label}
          sx={{
            px: 0.75,
            py: 0.2,
            borderRadius: 999,
            fontSize: 11,
            lineHeight: 1.4,
            fontWeight: 700,
            bgcolor:
              chip.tone === 'warning'
                ? '#fff7ed'
                : chip.tone === 'success'
                  ? '#ecfdf3'
                  : chip.tone === 'error'
                    ? '#fef3f2'
                    : chip.tone === 'info'
                      ? '#eff6ff'
                      : '#f2f4f7',
            color:
              chip.tone === 'warning'
                ? '#b54708'
                : chip.tone === 'success'
                  ? '#027a48'
                  : chip.tone === 'error'
                    ? '#b42318'
                    : chip.tone === 'info'
                      ? '#175cd3'
                      : '#475467',
          }}
        >
          {chip.label}
        </Box>
      ))}
    </Box>
  );
};

const deriveActionCopy = (row: any): { button: string; caption: string } => {
  if (row?.group_key) {
    const reviewerState = deriveReviewerCaseState(row);
    return {
      button: reviewerState.actionNeeded ? 'REVIEW PATTERN' : 'REVIEW PATTERN',
      caption: reviewerState.actionNeeded ? reviewerState.consequence : reviewerState.helper,
    };
  }

  const status = String(row?.status || '').trim().toUpperCase();
  const assignee = incidentCanonicalFollowupOwnerLabel(row) || String(row?.owner || '').trim();
  const workOrderLabel = row?.odoo_ticket_id ? `Work order ${row.odoo_ticket_id} linked` : '';

  if (status === 'CLOSED' || status === 'SUPPRESSED') {
    return {
      button: 'View record',
      caption: workOrderLabel || 'Resolution and evidence available',
    };
  }
  return {
    button: REVIEW_OBSERVATION_CTA,
    caption: assignee
      ? (workOrderLabel || `${displaySafetyManagerLabel('followup_owner')}: ${assignee}`)
      : (workOrderLabel || 'Review evidence and record disposition'),
  };
};

const rawEvidenceCount = (row: Incident) => {
  const attachments = Array.isArray(row.evidence?.attachments) ? row.evidence?.attachments?.length || 0 : 0;
  const inlineCount =
    (row.evidence?.latest?.snapshot?.filename ? 1 : 0) +
    ((row.evidence as any)?.latest?.video?.filename ? 1 : 0) +
    (((row as any).snapshot_status ? 1 : 0) + ((row as any).clip_status ? 1 : 0) + ((row as any).meta_status ? 1 : 0));
  return Math.max(attachments, inlineCount);
};

export function useIncidentsTableColumns({
  zoneNameById,
  cameraNameById,
  deviceNameById,
  locationLookups = {},
  incidentTypeLabel,
  incidentRowTypeLabel,
  incidentPolicyLabel,
  findingForIncident,
  severityRank,
  openEvidencePopover,
  openIncidentDetails,
  renderWorkOrderCell,
  handleEnsureOdooTicket,
  canManageGroupPatterns = false,
  groupPatternActionBusy = false,
  onGroupPatternAction,
  operatorMode = false,
}: UseIncidentsTableColumnsArgs) {
  const resolveRowLocation = useCallback((row: {
    location_snapshot?: any;
    envelope_metadata?: any;
    camera_id?: string | null;
    zone_id?: string | null;
  }) => {
    const zoneId = String(row?.zone_id || '').trim();
    const cameraId = String(row?.camera_id || '').trim();
    const zoneLabel = presentAreaLabel(zoneNameById.get(zoneId) || zoneId || '');
    const cameraName = String(cameraNameById.get(cameraId) || '').trim();
    return resolveSafetyLocationDisplay(
      {
        location_snapshot: row?.location_snapshot,
        envelope_metadata: row?.envelope_metadata,
        camera_id: cameraId,
        zone_id: zoneId,
        zoneLabel,
        cameraName,
      },
      locationLookups,
    );
  }, [cameraNameById, locationLookups, zoneNameById]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'incident',
        headerName: 'Observation',
        minWidth: 340,
        flex: 1.5,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as Incident;
          const title = incidentRowTypeLabel(row);
          const policyLabel = incidentPolicyLabel(row);
          const zoneId = String(row.zone_id || '').trim();
          const cameraId = String(row.camera_id || '').trim();
          const deviceId = String(row.device_id || '').trim();
          const summary = incidentDisplaySummaryForRow(row, {
            includeLocation: false,
            zoneName: zoneId ? zoneNameById.get(zoneId) : undefined,
            cameraName: cameraId ? cameraNameById.get(cameraId) : undefined,
            deviceName: deviceId ? deviceNameById.get(deviceId) : undefined,
          });
          const chips = incidentInsightChipsForRow(row, {
            includeOwnership: true,
            includeEvidence: false,
            includeDuration: false,
            includeRelatedAlerts: false,
            includeGrouped: false,
          }).filter((chip) =>
            chip.label.startsWith(`${displaySafetyManagerLabel('followup_owner')}: `) ||
            ['High confidence', 'Overdue', `${displaySafetyManagerLabel('followup_owner')} unassigned`, 'Mine'].includes(chip.label)
          );

          return (
            <Box sx={{ lineHeight: 1.2 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.875rem', letterSpacing: '-0.01em' }}>
                {title}
              </Typography>
              {summary && !(operatorMode && /unsafe proximity|danger zone|restricted/.test(summary.toLowerCase())) ? (
              <Typography variant="caption" color="text.secondary" sx={{ ...clampTextSx, lineHeight: 1.3 }}>
                {summary}
              </Typography>
              ) : null}
              {policyLabel && !operatorMode ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>
                  Detection rule: {policyLabel}
                </Typography>
              ) : null}
              {String((row as any)?.episode_id || '').trim() && String(row.incident_id || '').trim() ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>
                  Parent pattern {String(row.incident_id)}
                </Typography>
              ) : null}
              {renderInsightStrip(chips)}
            </Box>
          );
        },
      },
      {
        field: 'severity',
        headerName: 'Parent severity',
        width: 140,
        sortComparator: (v1: any, v2: any) => severityRank(v1) - severityRank(v2),
        renderCell: (params: GridRenderCellParams) => (
          <SeverityBadge severity={String((params.row as any)?.parent_severity || params.value || '')} />
        ),
      },
      {
        field: 'status',
        headerName: 'Parent incident status',
        width: 180,
        renderCell: (params: GridRenderCellParams) => {
          const presentation = observationParentStatusPresentation(params.row as any);
          return (
            <Box sx={{ lineHeight: 1.15 }}>
              {presentation.parentStatus ? (
                <StatusBadge status={presentation.parentStatus} />
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 700 }} data-testid="no-parent-incident">
                  {NO_PARENT_INCIDENT_LABEL}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
                Parent incident status
              </Typography>
              {presentation.episodeState ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="episode-state">
                  {`Episode ${presentation.episodeState}`}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        field: 'zone',
        headerName: 'Location',
        minWidth: 200,
        flex: 0.85,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const loc = resolveRowLocation(params.row as Incident);
          return renderLocation(loc.primary, loc.secondary || undefined);
        },
      },
      {
        field: 'target',
        headerName: operatorMode ? 'Camera' : 'Target',
        minWidth: 220,
        flex: 0.9,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as Incident;
          const loc = resolveRowLocation(row);
          const cameraId = String(row?.camera_id || '').trim();
          const cameraName = cameraNameById.get(cameraId) || loc.primary || cameraId || '—';
          const episodeCameraId = String((row as any)?.episode_id || '').trim() && cameraId && cameraName !== cameraId
            ? cameraId
            : '';
          if (operatorMode) {
            const viewLabel = /edge/i.test(String(cameraName))
              ? (loc.primary || cameraName)
              : (cameraName || loc.primary || '—');
            return renderTarget(viewLabel, episodeCameraId || undefined);
          }
          const cameraLabel = `Camera stream ${cameraNameById.get(cameraId) || cameraId || '—'}`;
          const deviceId = String(row?.device_id || '').trim();
          const deviceLabel = `Edge device ${deviceNameById.get(deviceId) || deviceId || '—'}`;
          const targetSecondary = [deviceLabel, episodeCameraId].filter(Boolean).join(' · ');
          return renderTarget(cameraLabel, targetSecondary);
        },
      },
      {
        field: 'last_seen',
        headerName: 'Time',
        minWidth: 160,
        flex: 0.7,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as any;
          const timestamp = row?.episode_id ? row?.qualified_at : resolveLastSeenTimestamp(row);
          return (
            <Box sx={{ lineHeight: 1.15 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatRelativeTime(timestamp)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {formatExactDateTime(timestamp)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'evidence',
        headerName: 'Evidence',
        minWidth: 185,
        flex: 0.9,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as Incident;
          const episodeId = String((row as any)?.episode_id || '').trim();
          const attachments = row.evidence?.attachments;
          const ownAttachments = Array.isArray(attachments)
            ? attachments.filter((item: any) => !episodeId || String(item?.episode_id || episodeId) === episodeId)
            : [];
          const count = episodeId ? ownAttachments.length : rawEvidenceCount(row);
          const ev = episodeId
            ? {
                main: String((row as any)?.evidence_status || (count ? `${count} evidence item${count === 1 ? '' : 's'}` : 'No evidence')),
                sub: String((row as any)?.terminal_reason || (count ? 'This episode' : 'No evidence row')),
              }
            : getRawModeEvidenceQueueCopy(row);
          return (
            <Box
              onMouseEnter={(event) => void openEvidencePopover(event as any, row)}
              sx={{ width: '100%' }}
            >
              <EvidenceCount count={count} primaryText={ev.main} caption={ev.sub} />
            </Box>
          );
        },
      },
      {
        field: 'action',
        headerName: 'Next step',
        width: 170,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as Incident;
          const actionCopy = deriveActionCopy(row);
          return (
            <Box sx={{ lineHeight: 1.1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={(event) => {
                  event.stopPropagation();
                  openIncidentDetails(row);
                }}
              >
                {actionCopy.button}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {actionCopy.caption}
              </Typography>
            </Box>
          );
        },
      },
    ],
    [
      cameraNameById,
      deviceNameById,
      incidentPolicyLabel,
      incidentRowTypeLabel,
      openEvidencePopover,
      openIncidentDetails,
      operatorMode,
      resolveRowLocation,
      severityRank,
      zoneNameById,
    ],
  );

  const groupColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'summary_text',
        headerName: 'Pattern',
        minWidth: 380,
        flex: 1.55,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IncidentGroup;
          const title = incidentRowTypeLabel(row as any);
          const policyLabel = incidentPolicyLabel(row as any);
          const zoneId = String(row.zone_id || '').trim();
          const cameraId = String(row.camera_id || '').trim();
          const deviceId = String(row.device_id || '').trim();
          const summary = incidentDisplaySummaryForRow(row as any, {
            includeLocation: false,
            zoneName: zoneId ? zoneNameById.get(zoneId) : undefined,
            cameraName: cameraId ? cameraNameById.get(cameraId) : undefined,
            deviceName: deviceId ? deviceNameById.get(deviceId) : undefined,
          });
          const chips = incidentInsightChipsForRow(row as any, {
            includeOwnership: true,
            includeEvidence: false,
            includeDuration: false,
            includeRelatedAlerts: true,
            includeGrouped: true,
          }).filter((chip) =>
            chip.label.startsWith('Recurring') ||
            chip.label.startsWith(`${displaySafetyManagerLabel('followup_owner')}: `) ||
            /^\d+ pattern observations$/.test(chip.label) ||
            ['High confidence', 'Recurring risk pattern', `${displaySafetyManagerLabel('followup_owner')} unassigned`, 'Mine'].includes(chip.label)
          );

          return (
            <Box sx={{ lineHeight: 1.2 }}>
              <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.875rem', letterSpacing: '-0.01em' }}>
                {title}
              </Typography>
              {summary && !(operatorMode && /unsafe proximity|danger zone|restricted/.test(summary.toLowerCase())) ? (
              <Typography variant="caption" color="text.secondary" sx={{ ...clampTextSx, lineHeight: 1.3 }}>
                {summary}
              </Typography>
              ) : null}
              {policyLabel && !operatorMode ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>
                  Detection rule: {policyLabel}
                </Typography>
              ) : null}
              {renderInsightStrip(chips)}
            </Box>
          );
        },
      },
      {
        field: 'severity',
        headerName: 'Severity',
        width: 120,
        sortComparator: (v1: any, v2: any) => severityRank(v1) - severityRank(v2),
        renderCell: (params: GridRenderCellParams) => <SeverityBadge severity={String(params.value || '')} />,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 220,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const reviewerState = deriveReviewerCaseState(params.row as IncidentGroup);
          const chipColor =
            reviewerState.tone === 'warning'
              ? 'warning'
              : reviewerState.tone === 'info'
                ? 'info'
                : reviewerState.tone === 'success'
                  ? 'success'
                  : 'default';
          return (
            <Box sx={{ lineHeight: 1.15 }}>
              <Chip
                size="small"
                label={reviewerState.label}
                color={chipColor}
                variant={reviewerState.tone === 'default' ? 'outlined' : 'filled'}
                sx={{ fontWeight: 800, maxWidth: '100%' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
                {reviewerState.helper}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'zone',
        headerName: 'Location',
        minWidth: 200,
        flex: 0.85,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const loc = resolveRowLocation(params.row as IncidentGroup);
          return renderLocation(loc.primary, loc.secondary || undefined);
        },
      },
      {
        field: 'target',
        headerName: operatorMode ? 'Camera' : 'Target',
        minWidth: 220,
        flex: 0.9,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IncidentGroup;
          const loc = resolveRowLocation(row);
          const cameraName = cameraNameById.get(String(row?.camera_id || '')) || loc.primary || row?.camera_id || '—';
          if (operatorMode) {
            const viewLabel = /edge/i.test(String(cameraName))
              ? (loc.primary || cameraName)
              : (cameraName || loc.primary || '—');
            return renderTarget(viewLabel);
          }
          const cameraLabel = `Camera stream ${cameraNameById.get(String(row?.camera_id || '')) || row?.camera_id || '—'}`;
          const deviceId = String(row?.device_id || '').trim();
          const deviceLabel = `Edge device ${deviceNameById.get(deviceId) || deviceId || '—'}`;
          return renderTarget(cameraLabel, deviceLabel);
        },
      },
      {
        field: 'last_seen_at',
        headerName: 'Last seen',
        minWidth: 170,
        flex: 0.7,
        sortable: true,
        valueGetter: (params) => timestampSortValue((params as GridRenderCellParams).row),
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IncidentGroup;
          const lastSeen = resolveLastSeenTimestamp(row);
          return (
            <Box sx={{ lineHeight: 1.15 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatRelativeTime(lastSeen)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {formatExactDateTime(lastSeen)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'evidence_count',
        headerName: 'Observations',
        minWidth: 185,
        flex: 0.9,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IncidentGroup;
          const n = Math.max(0, Number(row?.alert_count || 0));
          const ev = getGroupedModeEvidenceQueueCopy(row);
          return <EvidenceCount count={n} primaryText={ev.main} caption={ev.sub} />;
        },
      },
      ...(operatorMode || !canManageGroupPatterns ? [] : [{
        field: 'pattern_actions',
        headerName: 'More',
        minWidth: 72,
        width: 80,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          if (!onGroupPatternAction) return null;
          const row = params.row as IncidentGroup;
          return (
            <IncidentGroupPatternActions
              group={row}
              canManage={canManageGroupPatterns}
              busy={groupPatternActionBusy}
              onAction={onGroupPatternAction}
              compact
            />
          );
        },
      } as GridColDef]),
      {
        field: 'action',
        headerName: 'Next step',
        width: 168,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as IncidentGroup;
          const actionCopy = deriveActionCopy(row);
          return (
            <Box sx={{ lineHeight: 1.1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={(event) => {
                  event.stopPropagation();
                  openIncidentDetails(row);
                }}
              >
                {actionCopy.button}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {actionCopy.caption}
              </Typography>
            </Box>
          );
        },
      },
    ],
    [
      cameraNameById,
      canManageGroupPatterns,
      deviceNameById,
      groupPatternActionBusy,
      incidentPolicyLabel,
      incidentRowTypeLabel,
      onGroupPatternAction,
      openIncidentDetails,
      operatorMode,
      resolveRowLocation,
      severityRank,
      zoneNameById,
    ],
  );

  void incidentTypeLabel;
  void findingForIncident;
  void renderWorkOrderCell;
  void handleEnsureOdooTicket;

  return { columns, groupColumns };
}
